import { Building2, Users, ChevronRight } from 'lucide-react';
import AnimateIn from './AnimateIn';

const landlordFeatures = [
  'Compliance dashboard with clear action items',
  'Automated reminders for key dates and renewals',
  'Evidence tracking for inspections and safety checks',
  'Document expiry alerts so nothing lapses',
  'Rights-pack delivery workflows',
  'Property and tenancy organisation tools',
];

const tenantFeatures = [
  'Tenancy summary with all key details in one place',
  'Shared documents from your landlord, accessible anytime',
  'Repair and issue logging with photos and dates',
  'Reminders for your own key dates and actions',
  'Clear rights guidance in plain English',
  'Step-by-step next actions when things go wrong',
];

function AudienceCard({ id, icon: Icon, title, subtitle, features, accentColor, delay }) {
  return (
    <AnimateIn delay={delay} direction={delay === 0 ? 'left' : 'right'}>
      <div
        data-testid={`audience-card-${id}`}
        className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 h-full hover:shadow-md transition-shadow duration-300"
      >
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5`} style={{ backgroundColor: `${accentColor}15` }}>
          <Icon size={28} style={{ color: accentColor }} />
        </div>
        <h3 className="font-heading font-bold text-2xl text-ink mb-1">{title}</h3>
        <p className="font-body text-slate-text text-sm mb-6">{subtitle}</p>
        <ul className="space-y-3">
          {features.map((f, i) => (
            <li key={f} className="flex items-start gap-3 text-sm font-body text-ink/80">
              <ChevronRight size={16} className="mt-0.5 flex-shrink-0" style={{ color: accentColor }} />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>
    </AnimateIn>
  );
}

export default function AudienceSection() {
  return (
    <section id="landlords" data-testid="audience-section" className="py-20 sm:py-28 bg-mist">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimateIn>
          <div className="text-center mb-14">
            <span className="inline-block text-compliance-green font-body font-semibold text-sm tracking-wide uppercase mb-3">
              Built for both sides
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-ink mb-4">
              Whether you're a landlord or a tenant
            </h2>
            <p className="text-slate-text font-body text-lg max-w-2xl mx-auto">
              ActCheck is designed to help everyone in the tenancy relationship stay informed and organised.
            </p>
          </div>
        </AnimateIn>

        <div id="tenants" className="grid md:grid-cols-2 gap-6 lg:gap-8">
          <AudienceCard
            id="landlords"
            icon={Building2}
            title="For Landlords"
            subtitle="Stay compliant and reduce risk"
            features={landlordFeatures}
            accentColor="#102A5E"
            delay={0}
          />
          <AudienceCard
            id="tenants"
            icon={Users}
            title="For Tenants"
            subtitle="Know your rights and stay protected"
            features={tenantFeatures}
            accentColor="#27C281"
            delay={150}
          />
        </div>
      </div>
    </section>
  );
}
